import { EventEmitter } from 'events';
import IInputKeyMessage from './IInputKeyMessage';
import IKeyUpEvent, { KeyUpEventMap } from './IKeyUpEvent';
import validate from '../Validate/Validate';
import IInput from './IInput';

/**
 * The `sos.input` API groups together all input-related functionality, such as remote control key events. With this API, you can
 * listen to key events from the remote control and handle them in your application.
 */
export default class Input implements IInput {
	public static readonly MESSAGE_PREFIX: string = 'input';

	private readonly eventEmitter: EventEmitter = new EventEmitter();
	private readonly hasWindowEventListener: boolean = false;

	private alive: boolean = true;

	/** @internal */
	constructor(
		public readonly window: Window,
		private readonly messagePrefix: string,
	) {
		if (!this.isTopLevelFrame() && this.canAccessParentOrigin()) {
			this.hasWindowEventListener = true;
			this.window.addEventListener('keyup', this.dispatchEventToParent);
		}
	}

	/** @internal */
	public destroy() {
		if (this.alive) {
			this.alive = false;
			this.removeEventListeners();
			if (this.hasWindowEventListener) {
				this.window.removeEventListener('keyup', this.dispatchEventToParent);
			}
		}
	}

	/**
	 * The `onKeyUp` method sets up a listeners, which is called on every keystroke of the remote controller. For the specific logic of an
	 * application (games for example), binding the remote control inputs can be helpful. Only a subset of all remote control keys is
	 * supported on the specific device. Only numerical digits are guaranteed to be supported.
	 *
	 * :::note
	 * This feature must be tested by users on real devices.
	 * :::
	 *
	 * @param listener The listener function that will be called on every key up event.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @throws {Error} If listener is not valid function.
	 * @throws {Error} If listener is unregistered.
	 * @since 1.3.0
	 */
	public onKeyUp(listener: (event: IKeyUpEvent) => void): void {
		this.checkAlive();
		validate({ listener }).required().function();
		this.eventEmitter.addListener('keyup', listener);
	}

	/** @internal */
	public handleMessageData(data: IInputKeyMessage) {
		switch (data.type) {
			case this.getMessage('keyup'): {
				const keyupEvent: IKeyUpEvent = {
					type: 'keyup',
					keyCode: data.keyCode,
					keyName: data.keyName as keyof typeof KeyUpEventMap,
				};
				this.emitKeyUpEvent(keyupEvent);
				break;
			}
			default:
		}
	}

	/**
	 * The `removeEventListeners()` method removes all event listeners bind on `sos.input` object.
	 */
	public removeEventListeners(): void {
		this.eventEmitter.removeAllListeners();
	}

	/**
	 * The `removeEventListener()` method removes all event listeners for a specific event (only `keyup` is supported).
	 *
	 * @param event The name of the event to remove the listener for. Currently, only `keyup` is supported.
	 * @param listener The listener function to remove.
	 * @returns {void} Resolves when the listener is successfully removed.
	 */
	public removeEventListener(event: 'keyup', listener: (...args: any[]) => void): void {
		this.eventEmitter.removeListener(event, listener);
	}

	/** @internal */
	public emitKeyUpEvent(event: IKeyUpEvent) {
		this.checkAlive();
		this.eventEmitter.emit('keyup', event);
	}

	private isTopLevelFrame(): boolean {
		return this.window === this.window.parent;
	}

	private canAccessParentOrigin(): boolean {
		try {
			const hasParent = !!this.window?.parent?.document;
			if (!hasParent) {
				console.warn('Parent window or document is not defined');
			}
			return hasParent;
		} catch (e) {
			if (e instanceof DOMException) {
				// Blocked a frame with origin "..." from accessing a cross-origin frame.
				// This will happen on Android when applets have https://appassets.android.signageos.io origin
				// and top-level frame has file:// origin. Android doesn't benefit from this hack anyway.
				console.warn('Parent document is not accessible');
				return false;
			} else {
				throw e;
			}
		}
	}

	private dispatchEventToParent = (event: KeyboardEvent) => {
		try {
			this.window.parent.document.dispatchEvent(new KeyboardEvent('keyup', event));
		} catch (e) {
			if (e instanceof ReferenceError) {
				// KeyboardEvent constructor isn't available during tests.
				// TODO Mock KeyboardEvent constructor during tests.
				this.window.parent.document.dispatchEvent(event);
			} else {
				throw e;
			}
		}
	};

	private getMessage(name: string) {
		return this.messagePrefix + '.' + Input.MESSAGE_PREFIX + '.' + name;
	}

	private checkAlive() {
		if (!this.alive) {
			throw new Error('destroyed');
		}
	}
}
